import { HttpClient, type HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { catchError, combineLatest, map, type Observable, switchMap, take, throwError } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { ConsentService } from '@fcom/common/login/services/consent.service';
import {
  CmsCampaign,
  CmsDataService,
  ConfigService,
  getDestinationInCampaign,
  GlobalBookingTravelClass,
  SentryLogger,
} from '@fcom/core';
import { type Location, LocationType } from '@fcom/core-api';
import { LanguageService } from '@fcom/ui-translate';

import { DataCloudService } from '../../datacloud';
import { ConsentTextId } from '../../interfaces';
import type {
  FlightSearchEventPayload,
  PersonalizedDestinationLocation,
  TrendingDestinationLocation,
  TrendingDestinationsBaseLocation,
  TrendingDestinationsResponse,
} from '../../interfaces/recommendation.interface';
import { EventLabel } from '../../interfaces/recommendation.interface';

@Injectable()
export class RecommendationService {
  private configService = inject(ConfigService);
  private http = inject(HttpClient);
  private languageService = inject(LanguageService);
  private sentryLogger = inject(SentryLogger);
  private consentService = inject(ConsentService);
  private cmsDataService = inject(CmsDataService);
  private dataCloudService = inject(DataCloudService);

  sendFlightSearchEvent(
    origin: Location,
    destination: Location,
    departureDate: string,
    returnDate?: string,
    paxAdults?: number,
    paxChildren?: number
  ): Observable<HttpResponse<Response>> {
    if (!departureDate || !origin || !destination) {
      return throwError(() => 'Invalid parameters');
    }

    return this.consentService.getCookieConsentStatusById(ConsentTextId.COOKIE_ANALYTICS).pipe(
      switchMap((analyticsConsent) => {
        const finnairCookieId = this.consentService.getCookieIdFromSnippet();
        const payload: FlightSearchEventPayload = {
          locale: this.languageService.localeValue,
          requestId: uuid(),
          data: {
            label: EventLabel.SEARCH_CONNECTIONS,
            departureAirport: this.getLocationCodeForType(origin, LocationType.AIRPORT),
            arrivalAirport: this.getLocationCodeForType(destination, LocationType.AIRPORT),
            departureCity: this.getLocationCodeForType(origin, LocationType.CITY),
            arrivalCity: this.getLocationCodeForType(destination, LocationType.CITY),
            departureDate,
            returnDate,
            passengers: {
              main: paxAdults,
              children: paxChildren,
            },
          },
          ...(analyticsConsent && finnairCookieId && { sessionId: finnairCookieId }),
        };

        return this.http
          .post<HttpResponse<Response>>(`${this.configService.cfg.recommendationApiUrl}/events`, payload)
          .pipe(
            catchError((err: unknown) => {
              this.sentryLogger.warn('Failed to send flight search event to recommendation api', { error: err });
              return throwError(() => err);
            })
          );
      }),
      take(1)
    );
  }

  /** Get Trending Destinations
   * @param trendingDestinationLimit amount of trending destinations to be returned
   */
  getTrendingDestinations(
    origin: string,
    pageNumber = 0,
    trendingDestinationLimit = 30
  ): Observable<TrendingDestinationLocation[]> {
    return this._getTrendingDestinations(origin, trendingDestinationLimit, pageNumber * trendingDestinationLimit);
  }

  getTrendingDestinationsByContinent(origin: string, continentCode: string): Observable<TrendingDestinationLocation[]> {
    return this._getTrendingDestinations(origin, 200, 0, continentCode);
  }

  private _getTrendingDestinations(
    origin: string,
    limit = 0,
    offset = 0,
    continentCode?: string
  ): Observable<TrendingDestinationLocation[]> {
    const payload = {
      params: {
        locale: this.languageService.localeValue,
        limit,
        departureFrom: origin,
        offset,
      },
    };

    if (continentCode) {
      payload.params['continentCode'] = continentCode;
    }

    return combineLatest([
      this.http.get<TrendingDestinationsResponse<TrendingDestinationLocation>>(
        `${this.configService.cfg.recommendationApiUrl}/trending-destinations`,
        payload
      ),
      this.cmsDataService.getOngoingCampaigns(this.languageService.langValue, this.languageService.localeValue),
    ]).pipe(
      map(([trendingResponse, campaigns]) => {
        if (trendingResponse.locations.length === 0) {
          this.sentryLogger.warn('Get trending destination from recommendation api is empty');
        }
        return trendingResponse.locations.map(
          (location) => this.mapToCampaignLocation(location, campaigns, origin) as TrendingDestinationLocation
        );
      }),
      catchError((err: unknown) => {
        this.sentryLogger.warn('Failed to get trending destination from recommendation api', { error: err });
        return throwError(() => err);
      })
    );
  }

  getPersonalizedDestinations(origin: string): Observable<PersonalizedDestinationLocation[]> {
    return this.dataCloudService.getAnonymousId().pipe(
      take(1),
      switchMap((anonymousId) => {
        if (!anonymousId) {
          return throwError(() => new Error('Anonymous ID is not available'));
        }

        return combineLatest([
          this.http.post<TrendingDestinationsResponse<PersonalizedDestinationLocation>>(
            `${this.configService.cfg.recommendationApiUrl}/personalized-destinations`,
            {
              anonymousId,
              locale: this.languageService.localeValue,
            }
          ),
          this.cmsDataService.getOngoingCampaigns(this.languageService.langValue, this.languageService.localeValue),
        ]).pipe(
          map(([personalizedResponse, campaigns]) =>
            personalizedResponse.locations.map(
              (location) => this.mapToCampaignLocation(location, campaigns, origin) as TrendingDestinationLocation
            )
          ),
          catchError((err: unknown) => {
            this.sentryLogger.warn('Failed to get personalized destination from recommendation api', { error: err });
            return throwError(() => err);
          })
        );
      })
    );
  }

  private mapToCampaignLocation(
    location: TrendingDestinationsBaseLocation | TrendingDestinationLocation | PersonalizedDestinationLocation,
    campaigns: CmsCampaign[],
    origin: string
  ): TrendingDestinationsBaseLocation | TrendingDestinationLocation | PersonalizedDestinationLocation {
    const campaign = campaigns.find(
      (campaign) =>
        (campaign?.destinations?.some((dest: string) => dest === location.locationCode) ||
          campaign?.destinationsByOrigin?.[origin]?.some((dest: string) => dest === location.locationCode)) &&
        // Front page has hard coded economy prices
        campaign?.campaignTravelClass === GlobalBookingTravelClass.ECONOMY
    );
    return {
      ...location,
      isIncludedInCampaign: !!campaign,
      badgeLabel: campaign?.campaignLabel,
      enableBlackTeaser: getDestinationInCampaign(campaigns, location.locationCode)?.enableBlackTeaser,
    };
  }

  private getLocationCodeForType(location: Location, type: LocationType): string | undefined {
    return location?.type === type ? location.locationCityCode || location.locationCode : undefined;
  }
}
